import type Highcharts from "highcharts";
import type { Chart, Options, Point } from "highcharts";
import { renderToString } from "react-dom/server";

import { combineConfigs } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";

import { allTrackers } from "app/services/track/track";

import { TopicsHeatmapTooltip } from "./TopicsHeatmapTooltip";
import { computePointColor } from "./utils";
import type { HeatmapTreemapNode } from "./utils";

type FormatterContext = Point & {
    customData: HeatmapTreemapNode["customData"];
};

type ChartConfigParams = {
    onTopicClick: (topicName: string) => void;
    height: number;
    maxAbsChange: number;
    highlightedTopic?: string;
};

export const getChartConfig = ({ onTopicClick, height, maxAbsChange, highlightedTopic }: ChartConfigParams): Options =>
    combineConfigs({}, [
        {
            chart: {
                height,
                events: {
                    render: function (this: Chart) {
                        this.series[0]?.points?.forEach((point) => {
                            const { growth, change } = (point as unknown as FormatterContext).customData ?? {};
                            const color = computePointColor(growth, change, maxAbsChange);
                            point.color = color;
                            point.options.color = color;

                            const isHighlighted = highlightedTopic && point.name === highlightedTopic;
                            const graphic = point.graphic as Highcharts.SVGElement | undefined;

                            if (isHighlighted && point.shapeArgs) {
                                const { x, y, width: w, height: h } = point.shapeArgs;
                                const inset = 1.5;
                                graphic?.attr({
                                    fill: color,
                                    stroke: colorsPalettes.blue[400],
                                    "stroke-width": 3,
                                    x: x + inset,
                                    y: y + inset,
                                    width: Math.max(0, w - inset * 2),
                                    height: Math.max(0, h - inset * 2),
                                });
                            } else {
                                graphic?.attr({
                                    fill: color,
                                    stroke: colorsPalettes.carbon[0],
                                    "stroke-width": 1,
                                });
                            }
                        });
                    },
                },
            },
            plotOptions: {
                treemap: {
                    point: {
                        events: {
                            click: function (this: FormatterContext) {
                                const { name } = this;
                                allTrackers.dispatchTrackEvent("Button", "click", `Topics Heatmap/${name}`);
                                onTopicClick(name);
                            },
                        },
                    },
                    dataLabels: {
                        enabled: true,
                        useHTML: true,
                        style: {
                            fontSize: "11px",
                            fontWeight: "500",
                            color: colorsPalettes.carbon[500],
                            whiteSpace: "normal",
                            textOverflow: "clip",
                        },
                    },
                    cursor: "pointer",
                },
            },
            tooltip: {
                useHTML: true,
                outside: true,
                shape: "rect",
                hideDelay: 0,
                followPointer: true,
                shadow: {
                    color: colorsPalettes.black[0],
                    offsetX: 0,
                    offsetY: 0,
                    opacity: 0.08,
                    width: 8,
                },
                formatter: function (this: FormatterContext) {
                    const { volume, change, growth, trend } = this.customData;
                    return renderToString(
                        <TopicsHeatmapTooltip
                            name={this.name}
                            volume={volume}
                            change={change}
                            growth={growth}
                            trend={trend}
                        />,
                    );
                },
            },
        },
    ]);
